/**
 * Reading the outcome of a Content Management write.
 *
 * Why this exists: the four section pages used to `await apiRequest(...)` and then
 * unconditionally mark the section published, stamp "last saved" and navigate to
 * the next step — without looking at the response at all. A rejected save
 * therefore reported itself as a successful one, and the only way to find out was
 * the network tab. That is how an out-of-tier save looked before this: the screen
 * said published, the database had nothing.
 *
 * The backend does not use HTTP status for this. Every call comes back 200 with
 * `status` in the body: `0` for success, `1` with an `error_code` for failure (see
 * `services/apiRequest.js`, which only intercepts session errors and `ERROR_SQL`).
 * So a save is only successful if something actually checks that field — nothing
 * throws on its own.
 *
 * Enforcement stays server-side, as the task requires. This is about surfacing the
 * server's answer honestly, not about pre-empting it.
 */

/** The error codes the Content Management writes can answer with, mapped to copy. */
const SAVE_ERROR_MESSAGES = {
    // Tier: the caller lacks the Content Management tier this save needs. Market-level
    // saves require the `full` tier and are rejected outright; agency-level saves
    // require `sales_manager` but are only logged today (`ENFORCE_CONTENT_TIER =
    // false`), so in practice this code means a market-level save by a user who is
    // at most `sales_manager`.
    ERROR_NOT_AUTHORIZED_FOR_CONTENT_MANAGEMENT:
        "You do not have access to save this content. Saving to a whole market needs full Content Management access — ask an admin to check your access level.",

    // Scope/market: reachable from a stale market selection, or a bug in the payload
    // builder. Both are worth telling the user plainly rather than failing silently.
    ERROR_INVALID_CONTENT_SAVE_SCOPE:
        "This save could not be sent because its scope was not recognised. Reload the page and try again.",
    ERROR_INVALID_MARKET_CODE:
        "That market was not recognised. Pick the market again from the selector and retry.",
    ERROR_INVALID_CONTENT_SECTION:
        "This section was not recognised by the server. Reload the page and try again.",

    // Payload validation.
    ERROR_MISSING_REQUIRED_FIELDS: "Some required details were missing, so nothing was saved.",
    ERROR_INVALID_CATEGORY: "That item's type was not recognised, so nothing was saved.",
    ERROR_BRIEF_SHIP_DESTINATION_NOT_FOUND:
        "One of the items you picked is no longer available. Refresh the list and choose again.",

    // Cardinality — the counts the backend enforces per section.
    ERROR_MIN_3_FOCUS_SHIPS_REQUIRED: "Pick 3 focus ships before saving.",
    ERROR_MAX_3_FOCUS_SHIPS_ALLOWED: "Only 3 focus ships can be saved.",
    ERROR_MIN_2_FOCUS_DESTINATIONS_REQUIRED: "Pick 2 focus destinations before saving.",
    ERROR_MAX_2_FOCUS_DESTINATIONS_ALLOWED: "Only 2 focus destinations can be saved.",

    // Campaign upload.
    ERROR_ONLY_JPG_JPEG_ALLOWED: "Only JPG/JPEG files are allowed.",
    ERROR_INVALID_TIMEZONE: "That timezone was not recognised, so nothing was saved.",
};

/**
 * True when the backend accepted the write.
 *
 * Treats a missing `status` as failure rather than success: `apiRequest` resolves
 * with whatever came back, including a body that never reached the endpoint's
 * handler, and defaulting those to "saved" is the failure mode this file exists to
 * remove.
 */
export const isSaveOk = (response) => Number(response?.data?.status) === 0;

/**
 * A message for a rejected write, or `null` when it succeeded.
 *
 * An unmapped code falls back to the server's own `error_message` and, failing
 * that, shows the raw code — never a bare "something went wrong", because the code
 * is what makes a report from the field actionable.
 */
export const saveErrorMessage = (response) => {
    if (isSaveOk(response)) return null;

    const code = response?.data?.error_code;
    if (code && SAVE_ERROR_MESSAGES[code]) return SAVE_ERROR_MESSAGES[code];

    const serverMessage = response?.data?.error_message;
    if (serverMessage) return serverMessage;

    return code ? `Could not save (${code}).` : "Could not save — the server did not respond.";
};

/** True when the write was rejected for want of the right Content Management tier. */
export const isTierError = (response) =>
    response?.data?.error_code === "ERROR_NOT_AUTHORIZED_FOR_CONTENT_MANAGEMENT";
